import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";
import { auth, googleProvider } from "@data/firebase";
import { userProfileRepository } from "@data/firestore/UserProfileRepository";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Subscribe to the user's profile whenever auth changes. New users
  // get a doc with userType: null on first sign-in — the PersonaPicker
  // modal prompts them to pick.
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setProfileLoaded(false);
      return undefined;
    }
    const unsub = userProfileRepository.watch(user.uid, (next, loaded) => {
      if (next) {
        setUserProfile(next);
        setProfileLoaded(loaded);
      } else if (loaded) {
        // No profile yet — seed one with nulls so the persona picker
        // fires on first render. Seeding is idempotent.
        userProfileRepository
          .upsert(user.uid, {
            uid: user.uid,
            email: user.email || "",
            displayName: user.displayName || "",
            photoURL: user.photoURL || "",
            userType: null,
            tier: "free",
          })
          .catch((err) =>
            console.error("[AuthContext] seed profile failed", err)
          );
        setProfileLoaded(true);
      }
    });
    return unsub;
  }, [user]);

  const signInEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signUpEmail = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const signInGoogle = () => signInWithPopup(auth, googleProvider);

  const signOut = () => fbSignOut(auth);

  const setUserType = async (userType) => {
    if (!user) return;
    await userProfileRepository.setUserType(user.uid, userType);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        profileLoaded,
        loading,
        signInEmail,
        signUpEmail,
        signInGoogle,
        signOut,
        setUserType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
